import { FC } from 'react';
import Icon from '../icon';
import { Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Text from '../text';
import classNames from 'classnames';
import { IconType } from '../icon/helpers';

interface Props {
  label: string;
  icon?: IconType;
  color?: 'black' | 'grey' | 'link' | 'error' | 'light-grey';
  onClick: () => void;
  size?: 'x-small' | 'small' | 'medium';
  disabled?: boolean;
  dotdot?: boolean;
  inline?: boolean;
  className?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  flatButton: {
    cursor: 'pointer',
    '&.disabled': {
      cursor: 'default',
    },
  },
  link: {
    '&:hover:not(.disabled) .MuiSvgIcon-root': {
      color: theme.palette.link.hover,
    },
  },
  inline: {
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
    marginLeft: 6,
  },
}));

const FlatButton: FC<Props> = ({
  label,
  icon,
  onClick,
  color,
  size = 'medium',
  disabled = false,
  dotdot = true,
  inline = false,
  className = '',
}) => {
  const styles = useStyles();
  const isGrey = color === 'grey' || color === 'light-grey';
  const textColor = disabled ? 'grey' : color;
  const iconColor = disabled || isGrey ? 'grey' : color;
  const IconEl = icon ? <Icon name={icon} size={size} color={iconColor} /> : null;

  const handleClick = () => {
    if (disabled) {
      return;
    }

    onClick();
  };

  const compClass = classNames(styles.flatButton, inline && styles.inline, className, {
    disabled,
    [styles.link]: color === 'link' && !disabled,
  });

  return (
    <Stack
      className={compClass}
      flexDirection="row"
      alignItems="center"
      role="button"
      onClick={handleClick}
      gap={1}
    >
      {IconEl}
      <Text color={textColor} size={size} dotdot={dotdot}>
        {label}
      </Text>
    </Stack>
  );
};

export default FlatButton;
